import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { settingsService } from '../../services/settingsService';

// Async thunks
export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await settingsService.getSettings(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch settings');
    }
  }
);

export const fetchSettingById = createAsyncThunk(
  'settings/fetchSettingById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await settingsService.getSettingById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch setting');
    }
  }
);

export const createSetting = createAsyncThunk(
  'settings/createSetting',
  async (data, { rejectWithValue }) => {
    try {
      const response = await settingsService.createSetting(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create setting');
    }
  }
);

export const updateSetting = createAsyncThunk(
  'settings/updateSetting',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await settingsService.updateSetting(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update setting');
    }
  }
);

export const deleteSetting = createAsyncThunk(
  'settings/deleteSetting',
  async (id, { rejectWithValue }) => {
    try {
      await settingsService.deleteSetting(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete setting');
    }
  }
);

const initialState = {
  settings: [],
  currentSetting: null,
  loading: false,
  error: null,
  totalSettings: 0,
  success: false
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    clearCurrentSetting: (state) => {
      state.currentSetting = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch settings
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload.results || action.payload;
        state.totalSettings = action.payload.count || action.payload.length;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch setting by ID
      .addCase(fetchSettingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettingById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSetting = action.payload;
      })
      .addCase(fetchSettingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create setting
      .addCase(createSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSetting.fulfilled, (state, action) => {
        state.loading = false;
        state.settings.unshift(action.payload);
        state.totalSettings += 1;
        state.success = true;
      })
      .addCase(createSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update setting
      .addCase(updateSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSetting.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.settings.findIndex(setting => setting.id === action.payload.id);
        if (index !== -1) {
          state.settings[index] = action.payload;
        }
        if (state.currentSetting && state.currentSetting.id === action.payload.id) {
          state.currentSetting = action.payload;
        }
        state.success = true;
      })
      .addCase(updateSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete setting
      .addCase(deleteSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSetting.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = state.settings.filter(setting => setting.id !== action.payload);
        state.totalSettings -= 1;
        state.success = true;
      })
      .addCase(deleteSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSuccess, clearCurrentSetting } = settingsSlice.actions;
export default settingsSlice.reducer;
